import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
  Label,
  Alert,
} from "reactstrap";
import axios from "axios";
import Creatable from "../../../components/common/CreatableSelect";
import { END_POINT } from "../../../constants/defaultValues";
import {
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import "../../../assets/css/camera-import-modal.css";
import { roundImportPrice as roundPrice, formatImportPrice,
  mapAiImportItems, selectImportStock, reviewImportItem, prepareImportSubmission,
  remainingImportItems, invoiceComparison, importHeaderMetadata, compressImageForAI } from "./cameraImportUtils";

const formatNumberWithDot = (value) => typeof value === "string" ? value : formatImportPrice(value);

// Làm tròn số lượng tồn: tối đa 2 chữ số sau dấu chấm
const roundStockCount = (count) => {
  if (count === null || count === undefined || count === "") return 0;
  const num = Number(count);
  if (isNaN(num)) return 0;
  return Math.round((num + Number.EPSILON) * 1000) / 1000;
};

const getMatchPercent = (item) => {
  const score = item.match_score ?? item.confidence;
  if (score != null && Number.isFinite(Number(score))) {
    const val = Number(score);
    if (val >= 0.99) return 100;
    return Math.round(val * 100);
  }
  return 100;
};

const todayDate = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const formatFallbackReceiptCode = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const year = isNaN(d.getFullYear()) ? new Date().getFullYear() : d.getFullYear();
  const month = isNaN(d.getMonth()) ? String(new Date().getMonth() + 1).padStart(2, "0") : String(d.getMonth() + 1).padStart(2, "0");
  const day = isNaN(d.getDate()) ? String(new Date().getDate()).padStart(2, "0") : String(d.getDate()).padStart(2, "0");
  return `PN${year}${month}${day}01`;
};

const CameraImportModal = ({ isOpen, toggle, onSuccess, onApplyItems, applyOnly = false }) => {
  // Tabs: 'camera' (Live Camera & Barcode) | 'invoice' (Hóa đơn / Phiếu giao hàng) | 'upload' (Tải ảnh sản phẩm)
  const [activeTab, setActiveTab] = useState("camera");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("environment"); // 'environment' | 'user'
  const [cameraError, setCameraError] = useState("");
  const [capturedImage, setCapturedImage] = useState(null);
  const [sourceImages, setSourceImages] = useState([]);
  const [uploadMode, setUploadMode] = useState("invoice");
  const [isRetryPending, setIsRetryPending] = useState(false);
  const [conversionFactors, setConversionFactors] = useState({});
  const barcodeSupported = typeof window !== "undefined" && "BarcodeDetector" in window;

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // Live Barcode Scanner State
  const [isLiveScanning, setIsLiveScanning] = useState(true);
  const [isBarcodeFlashing, setIsBarcodeFlashing] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState("");

  // Hóa đơn bóc tách được (Invoice Info)
  const [invoiceInfo, setInvoiceInfo] = useState(null);

  // Dữ liệu sản phẩm sau khi AI quét hoặc Barcode phát hiện
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [transactionDate, setTransactionDate] = useState(todayDate);
  const [receiptCode, setReceiptCode] = useState(() => formatFallbackReceiptCode(todayDate()));
  const [providerName, setProviderName] = useState("");
  const [providerPhone, setProviderPhone] = useState("");
  const [providerAddress, setProviderAddress] = useState("");
  // Chiết khấu và thuế phí theo từng hóa đơn (key: source_batch_id)
  const [batchAdjustments, setBatchAdjustments] = useState({});

  const handleBatchAdjustmentChange = (batchId, field, value) => {
    setBatchAdjustments((prev) => ({
      ...prev,
      [batchId]: {
        ...(prev[batchId] || {}),
        [field]: value,
      },
    }));
  };

  // Hàm reset triệt để toàn bộ dữ liệu NCC & hóa đơn trước để hóa đơn sau không bị dính dữ liệu cũ
  const resetInvoiceState = () => {
    setInvoiceInfo(null);
    setProviderName("");
    setProviderPhone("");
    setProviderAddress("");
    setInvoiceForm({
      provider_name: "",
      tax_code: "",
      provider_phone: "",
      provider_address: "",
      invoice_number: "",
      invoice_date: "",
      note: "",
    });
    setIsEditingInvoice(false);
  };

  // Danh sách gợi ý Nhà cung cấp tổng hợp (bao gồm cả NCC vừa trích xuất từ hóa đơn)
  const providerOptions = React.useMemo(() => {
    const list = [...(providers || [])];
    const seen = new Set(list.map((p) => (p.name || "").trim().toLowerCase()));

    const addIfNew = (name) => {
      const clean = (name || "").trim();
      if (!clean || clean === "Nhập nhanh qua Camera AI") return;
      const lower = clean.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        list.push({ id: `p_${Date.now()}_${Math.random()}`, name: clean });
      }
    };

    if (invoiceInfo?.provider_name) addIfNew(invoiceInfo.provider_name);
    if (providerName) addIfNew(providerName);
    items.forEach((it) => {
      if (it?.provider_name) addIfNew(it.provider_name);
    });

    return list.map((p) => ({
      value: p.name,
      label: p.name,
    }));
  }, [providers, invoiceInfo, providerName, items]);

  // State chỉnh sửa thông tin Nhà cung cấp & Hóa đơn
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [syncInvoiceToAll, setSyncInvoiceToAll] = useState(true);
  const [invoiceForm, setInvoiceForm] = useState({
    provider_name: "",
    tax_code: "",
    provider_phone: "",
    provider_address: "",
    invoice_number: "",
    invoice_date: "",
    note: "",
  });

  // Tự động đồng bộ form chỉnh sửa khi có thông tin hóa đơn mới (KHÔNG fallback về state cũ)
  useEffect(() => {
    if (invoiceInfo) {
      setInvoiceForm({
        provider_name: invoiceInfo.provider_name || "",
        tax_code: invoiceInfo.tax_code || "",
        provider_phone: invoiceInfo.provider_phone || "",
        provider_address: invoiceInfo.provider_address || "",
        invoice_number: invoiceInfo.invoice_number || "",
        invoice_date: invoiceInfo.invoice_date || "",
        note: invoiceInfo.note || "",
      });
    }
  }, [invoiceInfo]);

  // Khi người dùng chọn 1 NCC trong dropdown form chỉnh sửa
  const handleSelectProviderInForm = (opt) => {
    const name = opt ? opt.value : "";
    const matchedP = providers.find(
      (p) => (p.name || "").trim().toLowerCase() === name.trim().toLowerCase()
    );
    setInvoiceForm((prev) => ({
      ...prev,
      provider_name: name,
      tax_code: matchedP?.tax_code || prev.tax_code || "",
      provider_phone: matchedP?.phone || prev.provider_phone || "",
      provider_address: matchedP?.address || prev.provider_address || "",
    }));
  };

  // Lưu thông tin NCC và áp dụng vào bảng
  const handleSaveInvoiceForm = () => {
    if (submissionInFlightRef.current || retryPendingRef.current) return;
    const trimmedProvider = (invoiceForm.provider_name || "").trim();
    const pPhone = (invoiceForm.provider_phone || "").trim();
    const pAddr = (invoiceForm.provider_address || "").trim();
    const pTax = (invoiceForm.tax_code || "").trim();
    const invNum = (invoiceForm.invoice_number || "").trim();
    const invDate = (invoiceForm.invoice_date || "").trim();
    const invNote = (invoiceForm.note || "").trim();

    // Lưu lại tên NCC trước khi thay đổi để biết items nào thuộc về NCC này
    const previousProvider = (invoiceInfo?.provider_name || providerName || "").trim();

    const updatedInvoice = {
      ...(invoiceInfo || {}),
      has_seller: Boolean(trimmedProvider),
      provider_name: trimmedProvider,
      tax_code: pTax,
      provider_phone: pPhone,
      provider_address: pAddr,
      invoice_number: invNum,
      invoice_date: invDate,
      note: invNote,
    };

    setInvoiceInfo(updatedInvoice);
    setProviderName(trimmedProvider);
    setProviderPhone(pPhone);
    setProviderAddress(pAddr);

    // Thêm vào danh sách gợi ý NCC nếu là NCC mới
    if (trimmedProvider) {
      setProviders((prev) => {
        const lower = trimmedProvider.toLowerCase();
        if (!prev.some((p) => (p.name || "").trim().toLowerCase() === lower)) {
          return [
            {
              id: `p_${Date.now()}`,
              name: trimmedProvider,
              phone: pPhone,
              address: pAddr,
              tax_code: pTax,
              notes: invNote,
            },
            ...prev,
          ];
        }
        return prev;
      });
    }

    // Nếu chọn tự động đồng bộ vào bảng: CHỈ áp dụng cho items thuộc về NCC này
    // (items chưa có NCC hoặc cùng NCC cũ trước khi chỉnh sửa), KHÔNG đụng items của NCC khác
    if (syncInvoiceToAll && items.length > 0) {
      const noteParts = [];
      if (invNum) noteParts.push(`Số HĐ: ${invNum}`);
      if (invNote) noteParts.push(invNote);
      const newNote = noteParts.join(" - ");

      const prevLower = previousProvider.toLowerCase();
      let updatedCount = 0;

      batchSubmissionRef.current = null;
      setItems((prev) =>
        prev.map((item) => {
          const itemProv = (item.provider_name || "").trim().toLowerCase();
          // Chỉ cập nhật nếu: item chưa có NCC, hoặc NCC cũ trùng với NCC đang chỉnh sửa
          const shouldUpdate = invoiceInfo?.source_batch_id
            ? item.source_batch_id === invoiceInfo.source_batch_id
            : !itemProv || itemProv === prevLower;

          if (shouldUpdate) {
            updatedCount++;
            return {
              ...item,
              provider_name: trimmedProvider || item.provider_name || "",
              provider_phone: pPhone, provider_address: pAddr, provider_tax_code: pTax, invoice_number: invNum,
              note: newNote || item.note || "",
              request_id: undefined,
            };
          }
          return { ...item, request_id: undefined };
        })
      );

      setIsEditingInvoice(false);
      const skippedCount = items.length - updatedCount;
      NotificatioSucController(
        skippedCount > 0
          ? `Đã cập nhật NCC "${trimmedProvider || "Nhà cung cấp"}" cho ${updatedCount} sản phẩm (${skippedCount} sản phẩm thuộc NCC khác không bị thay đổi).`
          : `Đã cập nhật NCC "${trimmedProvider || "Nhà cung cấp"}" cho toàn bộ ${updatedCount} sản phẩm!`
      );
    } else {
      setIsEditingInvoice(false);
      NotificatioSucController(`Đã cập nhật thông tin nhà cung cấp!`);
    }
  };

  // Nút 1-click áp dụng thông tin NCC hiện tại vào toàn bộ sản phẩm trong bảng
  const handleApplyInvoiceToAllItems = () => {
    if (submissionInFlightRef.current || retryPendingRef.current) return;
    const currentProvider = (invoiceInfo?.provider_name || providerName || invoiceForm.provider_name || "").trim();
    if (!currentProvider) {
      NotificatioErrController("Chưa có tên nhà cung cấp. Vui lòng bấm 'Nhập thông tin NCC' để điền.");
      setIsEditingInvoice(true);
      return;
    }

    const noteParts = [];
    const invNum = invoiceInfo?.invoice_number || invoiceForm.invoice_number || "";
    const invNote = invoiceInfo?.note || invoiceForm.note || "";
    if (invNum) noteParts.push(`Số HĐ: ${invNum.trim()}`);
    if (invNote) noteParts.push(invNote.trim());
    const newNote = noteParts.join(" - ");

    batchSubmissionRef.current = null;
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        provider_name: currentProvider,
        provider_phone: providerPhone, provider_address: providerAddress,
        provider_tax_code: invoiceInfo?.tax_code || invoiceForm.tax_code || "", invoice_number: invNum,
        note: newNote || item.note || "",
        request_id: undefined,
      }))
    );

    NotificatioSucController(`Đã gán NCC "${currentProvider}" cho toàn bộ ${items.length} sản phẩm!`);
  };

  // Làm mới danh sách và xóa toàn bộ dữ liệu quét để bắt đầu hóa đơn mới hoàn toàn
  const handleClearAllAndReset = () => {
    if (isRetryPending) return;
    setSourceImages([]);
    setCapturedImage(null);
    setApiError("");
    setItems([]);
    setBatchAdjustments({});
    resetInvoiceState();
    if (invoiceFileInputRef.current) invoiceFileInputRef.current.value = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
    batchSubmissionRef.current = null;
    NotificatioSucController("Đã làm mới danh sách. Bạn có thể quét hoặc tải hóa đơn mới.");
  };

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const invoiceFileInputRef = useRef(null);
  const batchSubmissionRef = useRef(null);
  const submissionInFlightRef = useRef(false);
  const retryPendingRef = useRef(false);
  const analysisSessionRef = useRef(0);
  const cameraRequestRef = useRef(0);
  const fileQueueRef = useRef(false);
  const lastScannedBarcodeRef = useRef({ code: "", timestamp: 0 });

  const selectAllInput = (el) => {
    if (!el) return;
    try {
      if (typeof el.select === "function") {
        el.select();
      }
    } catch (_) {}
  };

  const handleInputFocus = (e) => {
    selectAllInput(e.target);
  };

  const handleInputClick = (e) => {
    selectAllInput(e.target);
  };

  // Tải danh sách nhà cung cấp và mã phiếu nhập khi modal mở
  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem("access_token");
      axios
        .get(`${END_POINT}/company-provider`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data?.data) setProviders(res.data.data);
        })
        .catch(() => {});

      const targetDate = transactionDate || todayDate();
      axios
        .get(`${END_POINT}/warehouse-import/next-code?date=${targetDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data?.data) {
            setReceiptCode(res.data.data);
          }
        })
        .catch(() => {
          setReceiptCode((prev) => {
            if (prev && prev.startsWith("PN")) return prev;
            return formatFallbackReceiptCode(targetDate);
          });
        });
    }
  }, [isOpen, transactionDate]);

  // Phát âm thanh Beep khi camera quét trúng mã vạch
  const playBeep = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.12);
      osc.onended = () => ctx.close();
    } catch (e) {
      // Audio context might fail without user interaction, ignore
    }
  };

  // Khởi động Camera khi modal mở và đang ở tab dùng Camera
  useEffect(() => {
    if (
      isOpen &&
      (activeTab === "camera" || activeTab === "invoice") &&
      !capturedImage
    ) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, activeTab, cameraFacing, capturedImage]);

  // Reset state khi đóng modal
  useEffect(() => {
    if (!isOpen) {
      analysisSessionRef.current += 1;
      fileQueueRef.current = false;
      setSourceImages([]);
      setIsAnalyzing(false);
      setIsRetryPending(false);
      retryPendingRef.current = false;
      stopCamera();
      setCapturedImage(null);
      setCameraError("");
      setApiError("");
      setItems([]);
      resetInvoiceState();
      setLastScannedCode("");
      batchSubmissionRef.current = null;
    }
  }, [isOpen]);

  const startCamera = async () => {
    stopCamera();
    const cameraRequest = cameraRequestRef.current;
    setCameraError("");
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError(
          "Trình duyệt không hỗ trợ truy cập máy ảnh hoặc kết nối không phải HTTPS.",
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacing,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (cameraRequest !== cameraRequestRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(true);
    } catch (err) {
      console.warn("Lỗi mở camera:", err);
      setCameraError(
        "Không thể mở camera. Vui lòng cấp quyền truy cập máy ảnh hoặc chuyển sang tải ảnh có sẵn.",
      );
      setIsCameraActive(false);
    }
  };

  const stopCamera = () => {
    cameraRequestRef.current += 1;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const switchCameraFacing = () => {
    setCameraFacing((prev) =>
      prev === "environment" ? "user" : "environment",
    );
  };

  // Vòng lặp Real-time Barcode Detection trực tiếp trên Video Stream
  useEffect(() => {
    if (
      !isOpen ||
      activeTab !== "camera" ||
      !isCameraActive ||
      isAnalyzing ||
      capturedImage ||
      !isLiveScanning || isSubmitting || isRetryPending
    ) {
      return;
    }

    let isMounted = true;
    let detector = null;

    if ("BarcodeDetector" in window) {
      try {
        detector = new window.BarcodeDetector({
          formats: [
            "ean_13",
            "ean_8",
            "upc_a",
            "upc_e",
            "code_128",
            "code_39",
            "qr_code",
          ],
        });
      } catch (e) {
        detector = null;
      }
    }

    const scanInterval = setInterval(async () => {
      if (!isMounted || !videoRef.current || videoRef.current.readyState < 2)
        return;
      if (!detector) return;

      try {
        const barcodes = await detector.detect(videoRef.current);
        if (barcodes && barcodes.length > 0) {
          const rawCode = barcodes[0].rawValue;
          if (rawCode && rawCode.trim().length >= 8) {
            handleLiveBarcodeDetected(rawCode.trim());
          }
        }
      } catch (err) {
        // frame couldn't decode, normal
      }
    }, 320);

    return () => {
      isMounted = false;
      clearInterval(scanInterval);
    };
  }, [
    isOpen,
    activeTab,
    isCameraActive,
    isAnalyzing,
    capturedImage,
    isLiveScanning, isSubmitting, isRetryPending,
  ]);

  // Xử lý khi camera phát hiện mã vạch theo thời gian thực (Live Scan)
  const handleLiveBarcodeDetected = async (barcode) => {
    const now = Date.now();
    if (
      lastScannedBarcodeRef.current.code === barcode &&
      now - lastScannedBarcodeRef.current.timestamp < 1800
    ) {
      return; // Tránh quét lặp liên tục
    }

    lastScannedBarcodeRef.current = { code: barcode, timestamp: now };
    setLastScannedCode(barcode);
    playBeep();
    setIsBarcodeFlashing(true);
    setTimeout(() => setIsBarcodeFlashing(false), 500);

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${END_POINT}/stock/quick-barcode?barcode=${encodeURIComponent(barcode)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (submissionInFlightRef.current || retryPendingRef.current) return;
      const res = response.data;
      if (res.status === 200 && res.found && res.data) {
        const itemData = res.data;
        batchSubmissionRef.current = null;
        setItems((prev) => {
          const current = prev.map((item) => ({
            ...item,
            request_id: undefined,
          }));
          const existingIdx = current.findIndex(
            (p) =>
              (p.barcode && String(p.barcode).trim() === barcode) ||
              (p.stock_id && p.stock_id === itemData.stock_id),
          );

          if (existingIdx !== -1) {
            const updated = [...current];
            const currentQty = Number(updated[existingIdx].quantity) || 1;
            updated[existingIdx] = {
              ...updated[existingIdx],
              quantity: currentQty + 1,
            };
            NotificatioSucController(
              `+1 ${updated[existingIdx].name} (Tổng: ${currentQty + 1})`,
            );
            return updated;
          } else {
            const rawRetail = (itemData.retail_price !== undefined && itemData.retail_price !== null && itemData.retail_price !== "")
              ? itemData.retail_price
              : (itemData.suggested_price || "");
            const newItem = {
              id: `scan_${Date.now()}_${Math.random()}`,
              name: itemData.name,
              barcode: itemData.barcode,
              unit: itemData.unit || "cái",
              quantity: 1,
              price: roundPrice(itemData.price || 0),
              retail_price: rawRetail !== "" && Number(rawRetail) > 0 ? roundPrice(rawRetail) : "",
              retail_price_edited: false,
              provider_name: itemData.provider_name || (providerName !== "Nhập nhanh qua Camera AI" ? providerName : "") || "",
              note: "",
              expiry_date: "",
              stock_id: itemData.stock_id || null,
              matched_stock: itemData,
              match_type: itemData.match_type || (res.is_from_template ? "template_barcode" : "barcode_exact"),
              match_score: 1.0,
              is_matched: true,
              is_template: !!res.is_from_template || !!itemData.is_template,
            };
            const labelText = (res.is_from_template || itemData.is_template)
              ? `Tìm thấy sản phẩm mẫu: ${itemData.name}`
              : `Đã quét mã: ${itemData.name}`;
            NotificatioSucController(labelText);
            return [newItem, ...current];
          }
        });

        // Điền nhà cung cấp nếu có trong lịch sử
        if (
          itemData.provider_name &&
          (!providerName || providerName === "Nhập nhanh qua Camera AI")
        ) {
          setProviderName(itemData.provider_name);
        }
        if (itemData.provider_phone && !providerPhone) {
          setProviderPhone(itemData.provider_phone);
        }
      } else {
        // Chưa có trong kho
        batchSubmissionRef.current = null;
        setItems((prev) => {
          const current = prev.map((item) => ({
            ...item,
            request_id: undefined,
          }));
          const exists = current.find((p) => p.barcode === barcode);
          if (exists) return current;

          const newItem = {
            id: `scan_new_${Date.now()}`,
            name: "",
            barcode: barcode,
            unit: "cái",
            quantity: 1,
            price: 0,
            retail_price: "",
            provider_name: providerName !== "Nhập nhanh qua Camera AI" ? providerName : "",
            note: "",
            expiry_date: "",
            stock_id: null,
            matched_stock: null,
            is_matched: false,
          };
          NotificatioErrController(
            `Mã vạch ${barcode} chưa có trong kho. Bạn có thể nhập tên và giá để lưu vào kho.`,
          );
          return [newItem, ...current];
        });
      }
    } catch (err) {
      console.error("Lỗi quick-barcode:", err);
    }
  };

  // Chụp ảnh từ video stream (áp dụng cho cả sản phẩm và hóa đơn)
  const handleCapturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const rawDataUrl = canvas.toDataURL("image/jpeg", 0.9);
    // Nén ảnh trước khi gửi AI — giảm payload 5-10x
    const dataUrl = await compressImageForAI(rawDataUrl);
    setCapturedImage(dataUrl);
    stopCamera();

    // Tự động phân tích ảnh theo tab hiện tại (invoice hoặc product)
    analyzeImageWithAI(
      dataUrl,
      activeTab === "invoice" ? "invoice" : "product",
    );
  };

  const handleFiles = async (files, mode = uploadMode) => {
    if (fileQueueRef.current || isAnalyzing || isSubmitting || isRetryPending) return;
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const invalid = selected.find((file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024);
    if (invalid) { setApiError("Chọn ảnh JPG, PNG hoặc WebP, tối đa 10 MB mỗi ảnh."); return; }
    if (selected.length > 10) { setApiError("Mỗi lần tải tối đa 10 ảnh."); return; }
    fileQueueRef.current = true;
    const session = analysisSessionRef.current;
    try {
      for (const file of selected) {
        if (session !== analysisSessionRef.current) break;
        const rawDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = () => reject(new Error("Không đọc được ảnh đã chọn."));
          reader.readAsDataURL(file);
        });
        if (session !== analysisSessionRef.current) break;
        // Nén ảnh trước khi gửi AI — giảm payload 5-10x
        const dataUrl = await compressImageForAI(rawDataUrl);
        setCapturedImage(dataUrl);
        await analyzeImageWithAI(dataUrl, mode);
      }
    } catch (error) { setApiError(error.message); }
    finally { fileQueueRef.current = false; }
  };

  const handleFileChange = (e, mode = uploadMode) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    handleFiles(files, mode);
  };

  // Gửi ảnh sang server để phân tích AI
  const analyzeImageWithAI = async (imageDataUrl, mode = "product") => {
    const session = analysisSessionRef.current;
    const sourceId = uuidv4();
    setSourceImages((prev) => [...prev, { id: sourceId, image: imageDataUrl, mode }]);
    setIsAnalyzing(true);
    setApiError("");
    // Reset sạch toàn bộ thông tin NCC & Hóa đơn cũ trước khi nhận kết quả phân tích mới
    resetInvoiceState();

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/stock/import-by-image`,
        {
          imageBase64: imageDataUrl,
          mimeType: imageDataUrl.match(/^data:([^;]+);/)?.[1] || "image/jpeg",
          mode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 120000,
        },
      );

      if (session !== analysisSessionRef.current) return;
      const resData = response.data;
      console.log("[CameraImportModal] AI Response Data:", resData);
      if (resData.status === 200 && resData.data) {
        const { matched = [], unmatched = [], invoice_info } = resData.data;
        console.log("[CameraImportModal] invoice_info:", invoice_info);

        // Quy tắc cốt lõi: Phân biệt rõ Người bán vs Người mua
        // Nếu hóa đơn chỉ có người mua (has_seller === false hoặc không có tên bên bán)
        // -> KHÔNG ĐƯỢC TỰ ĐỘNG ĐIỀN THÔNG TIN NCC, nhưng hiển thị form để người dùng tự nhập tay!
        const hasSeller =
          invoice_info?.has_seller !== false &&
          Boolean(invoice_info?.provider_name && invoice_info.provider_name.trim());

        const pName = hasSeller ? invoice_info.provider_name.trim() : "";
        const pPhone = hasSeller ? (invoice_info.provider_phone || "").trim() : "";
        const pAddr = hasSeller ? (invoice_info.provider_address || "").trim() : "";
        const pTax = hasSeller ? (invoice_info.tax_code || "").trim() : "";
        const invNum = (invoice_info?.invoice_number || "").trim();
        const invDate = (invoice_info?.invoice_date || "").trim();
        const invNote = (invoice_info?.note || "").trim();

        let invoiceNote = "";
        const noteParts = [];
        if (invNum) noteParts.push(`Số HĐ: ${invNum}`);
        if (invNote) noteParts.push(invNote);
        invoiceNote = noteParts.join(" - ");

        // Gán đè trực tiếp, TUYỆT ĐỐI KHÔNG FALLBACK VỀ STATE CŨ CỦA HÓA ĐƠN TRƯỚC
        setInvoiceInfo(
          invoice_info
            ? {
                ...invoice_info,
                source_batch_id: sourceId,
                has_seller: hasSeller,
                provider_name: pName,
                provider_phone: pPhone,
                provider_address: pAddr,
                tax_code: pTax,
              }
            : null
        );

        setProviderName(pName);
        setProviderPhone(pPhone);
        setProviderAddress(pAddr);

        // Chiết khấu & thuế phí từ hóa đơn được lưu riêng theo batchId (sourceId)
        const aiDiscount = invoice_info?.discount_amount;
        const aiTax = invoice_info?.tax_amount;
        const discountVal =
          aiDiscount != null && Number.isFinite(Number(aiDiscount)) && Number(aiDiscount) > 0
            ? Number(aiDiscount)
            : "";
        const taxVal =
          aiTax != null && Number.isFinite(Number(aiTax)) && Number(aiTax) > 0
            ? Number(aiTax)
            : "";

        setBatchAdjustments((prev) => ({
          ...prev,
          [sourceId]: {
            discount_amount: discountVal,
            tax_amount: taxVal,
            invoice_number: invNum,
            provider_name: pName,
          },
        }));

        setInvoiceForm({
          provider_name: pName,
          tax_code: pTax,
          provider_phone: pPhone,
          provider_address: pAddr,
          invoice_number: invNum,
          invoice_date: invDate,
          note: invNote,
        });

        // Nếu hóa đơn CHỈ CÓ NGƯỜI MUA (không có người bán), tự động mở sẵn form để người dùng tự nhập tay
        if (!hasSeller) {
          setIsEditingInvoice(true);
        } else {
          setIsEditingInvoice(false);
        }

        if (pName) {
          setProviders((prev) => {
            const trimmed = pName.toLowerCase();
            const exists = prev.some(
              (p) => (p.name || "").trim().toLowerCase() === trimmed
            );
            if (!exists) {
              return [
                {
                  id: `temp_${Date.now()}`,
                  name: pName,
                  phone: pPhone,
                  address: pAddr,
                  tax_code: pTax,
                  notes: invoiceNote,
                },
                ...prev,
              ];
            }
            return prev;
          });
        }

        // NCC mặc định cho các dòng sản phẩm: nếu hóa đơn chỉ có người mua thì để trống ""
        const initialProvider = hasSeller ? pName : "";

        const newItems = mapAiImportItems({ matched, unmatched }, {
          providerName: initialProvider, phone: pPhone, address: pAddr, taxCode: pTax,
          invoiceNumber: invNum, note: invoiceNote, sourceId,
        }, uuidv4);

        if (newItems.length === 0) {
          if (invoice_info && (pName || invNum || pTax)) {
            const emptyItem = {
              id: `u_${Date.now()}`,
              name: "",
              barcode: "",
              unit: "cái",
              quantity: 1,
              price: 0,
              retail_price: "",
              provider_name: initialProvider,
              provider_phone: pPhone, provider_address: pAddr, provider_tax_code: pTax,
              invoice_number: invNum, source_batch_id: sourceId,
              note: invoiceNote || "",
              expiry_date: "",
              stock_id: null,
              matched_stock: null,
              is_matched: false,
            };
            batchSubmissionRef.current = null;
            setItems((prev) => [...prev, emptyItem]);
            NotificatioSucController(
              pName
                ? `Đã nhận diện hóa đơn NCC: ${pName}. Vui lòng nhập danh sách sản phẩm bên dưới.`
                : `Đã nhận diện số hóa đơn: ${invNum || "Hóa đơn"}. Vui lòng nhập NCC và danh sách sản phẩm bên dưới.`
            );
          } else {
            setApiError(
              mode === "invoice"
                ? "AI không nhận diện được bảng kê hàng trên hóa đơn này. Bạn có thể chụp lại rõ hơn hoặc tự thêm dòng."
                : "AI chưa nhận diện được sản phẩm rõ ràng. Bạn có thể chụp lại rõ hơn hoặc tự thêm dòng bên dưới.",
            );
          }
        } else {
          batchSubmissionRef.current = null;
          setItems((prev) => [
            ...prev.map((item) => ({ ...item, request_id: undefined })),
            ...newItems,
          ]);
          const usedProvider = resData.provider_used || resData.data?.provider_used;
          const fallbackTag = resData.fallback_triggered ? ` (Dự phòng ${usedProvider})` : "";
          if (!hasSeller) {
            NotificatioSucController(
              `Đã bóc tách ${newItems.length} mặt hàng. Hóa đơn chưa có Nhà Cung Cấp, bạn có thể tự nhập ở khung bên trên.`
            );
          } else {
            NotificatioSucController(
              mode === "invoice"
                ? `Đã bóc tách ${newItems.length} mặt hàng từ hóa đơn${fallbackTag}`
                : `Nhận diện ${newItems.length} sản phẩm (${matched.length} khớp kho, ${unmatched.length} mới)${fallbackTag}`,
            );
          }
        }
      } else {
        setApiError(resData.responseText || "Không thể phân tích ảnh");
      }
    } catch (err) {
      if (session !== analysisSessionRef.current) return;
      console.error("Lỗi phân tích AI:", err);
      const errMsg =
        err.response?.data?.responseText ||
        err.message ||
        "Lỗi kết nối khi phân tích ảnh AI";
      setApiError(errMsg);
      NotificatioErrController(errMsg);
    } finally {
      if (session === analysisSessionRef.current) setIsAnalyzing(false);
    }
  };

  // Cập nhật thông tin dòng sản phẩm
  const handleItemChange = (index, field, value) => {
    if (isRetryPending) return;
    batchSubmissionRef.current = null;
    setItems((prev) => {
      const updated = prev.map((item) => ({ ...item, request_id: undefined }));
      let finalVal = value;
      if (field === "quantity") {
        if (value === "" || value === null || value === undefined) {
          finalVal = "";
        } else {
          const num = Number(value);
          finalVal = isNaN(num) ? value : num;
        }
      } else if (field === "price" || field === "retail_price") {
        if (value === "" || value === null || value === undefined) {
          finalVal = "";
        } else {
          finalVal = value;
        }
      }
      updated[index] = {
        ...updated[index],
        [field]: finalVal,
        submission_error: undefined,
        ...(field === "retail_price" ? { retail_price_edited: true } : {}),
        ...(field === "price" ? { price_source: "manual" } : {}),
        ...(field === "provider_name" ? {
          provider_phone: providers.find((provider) => provider.name === value)?.phone || "",
          provider_address: providers.find((provider) => provider.name === value)?.address || "",
          provider_tax_code: providers.find((provider) => provider.name === value)?.tax_code || "",
        } : {}),
        ...(["name", "barcode", "unit"].includes(field) && updated[index].stock_id ? {
          stock_id: null, is_matched: false, requires_match_confirmation: true,
          stock_selection: "new",
          candidate_suggestions: [updated[index].matched_stock, ...(updated[index].candidate_suggestions || [])].filter(Boolean),
        } : {}),
      };
      return updated;
    });
  };

  // Xóa 1 dòng sản phẩm
  const handleRemoveItem = (index) => {
    batchSubmissionRef.current = null;
    setItems((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((item) => ({ ...item, request_id: undefined })),
    );
  };

  // Thêm 1 dòng thủ công
  const handleAddManualRow = () => {
    if (isRetryPending) return;
    batchSubmissionRef.current = null;
    setItems((prev) => [
      ...prev.map((item) => ({ ...item, request_id: undefined })),
      {
        id: `manual_${Date.now()}`,
        name: "",
        barcode: "",
        unit: "cái",
        quantity: 1,
        price: 0,
        retail_price: "",
        provider_name: providerName || "",
        provider_phone: providerPhone, provider_address: providerAddress,
        provider_tax_code: invoiceInfo?.tax_code || "",
        invoice_number: invoiceInfo?.invoice_number || "",
        source_batch_id: invoiceInfo?.source_batch_id || (sourceImages[sourceImages.length - 1]?.id || "manual"),
        note: "",
        expiry_date: "",
        stock_id: null,
        matched_stock: null,
        is_matched: false,
      },
    ]);
  };

  // Chụp thêm ảnh
  const handleRetakeOrAddMore = () => {
    setCapturedImage(null);
    setApiError("");
    resetInvoiceState(); // RESET triệt để để quét hóa đơn tiếp theo không bị nhớ địa chỉ/SĐT cũ
    if (invoiceFileInputRef.current) invoiceFileInputRef.current.value = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (activeTab === "camera" || activeTab === "invoice") {
      startCamera();
    }
  };

  const handleChooseStock = (index, stock) => {
    try {
      const selected = selectImportStock(items[index], stock, conversionFactors[items[index].id] || "");
      batchSubmissionRef.current = null;
      setItems((prev) => prev.map((item, i) => i === index ? selected : { ...item, request_id: undefined }));
    } catch (error) { NotificatioErrController(error.message); }
  };

  const handleSubmitBatch = async () => {
    if (submissionInFlightRef.current || !items.length) return;
    for (let i = 0; i < items.length; i++) {
      const error = reviewImportItem(items[i]);
      if (error) { NotificatioErrController("Dòng " + (i + 1) + ": " + error); return; }
    }
    // Phân bổ chiết khấu và thuế phí theo từng hóa đơn riêng biệt khi submit
    let itemsToProcess = items;
    const hasAnyAdjustment = Object.values(batchAdjustments).some((adj) => {
      const d = Number(adj?.discount_amount) || 0;
      const t = Number(adj?.tax_amount) || 0;
      return d > 0 || t > 0;
    });

    if (hasAnyAdjustment && totalAmount > 0) {
      // Tính tổng tiền hàng theo từng hóa đơn (batchId)
      const batchSubtotals = {};
      items.forEach((item) => {
        const bId = item.source_batch_id || "default";
        const lineTotal = (Number(item.quantity) || 0) * roundPrice(item.price);
        batchSubtotals[bId] = (batchSubtotals[bId] || 0) + lineTotal;
      });

      itemsToProcess = items.map((item) => {
        const bId = item.source_batch_id || "default";
        const bSubtotal = batchSubtotals[bId] || 0;
        const adj = batchAdjustments[bId] || {};
        const bDiscount = Number(adj.discount_amount) || 0;
        const bTax = Number(adj.tax_amount) || 0;

        if ((bDiscount > 0 || bTax > 0) && bSubtotal > 0) {
          const bFinal = bSubtotal - bDiscount + bTax;
          const ratio = Math.max(0, bFinal) / bSubtotal;
          const origPrice = roundPrice(item.price);
          const adjustedPrice = Math.max(0, Math.round(origPrice * ratio * 100) / 100);
          return {
            ...item,
            price: adjustedPrice,
            source_price: item.source_price != null ? item.source_price : origPrice,
          };
        }
        return item;
      });
    }

    const header = importHeaderMetadata(itemsToProcess);
    let invoiceNotes = header.notes || "";
    const noteParts = [];
    if (totalDiscount > 0) noteParts.push(`CK: -${formatNumberWithDot(totalDiscount)}đ`);
    if (totalTax > 0) noteParts.push(`VAT: +${formatNumberWithDot(totalTax)}đ`);
    if (noteParts.length > 0) {
      invoiceNotes = invoiceNotes ? `${invoiceNotes} (${noteParts.join(", ")})` : noteParts.join(", ");
    }

    const meta = {
      ...header,
      notes: invoiceNotes,
      code: receiptCode.trim(),
      name: header.provider_name,
      transaction_date: transactionDate,
    };
    if (applyOnly && onApplyItems) {
      onApplyItems(itemsToProcess.map((item) => ({ ...item, transaction_date: transactionDate })), {
        providerName: header.provider_name, transactionDate, invoiceInfo,
      });
      return;
    }
    const submission = isRetryPending && batchSubmissionRef.current
      ? batchSubmissionRef.current : prepareImportSubmission(itemsToProcess, meta, batchSubmissionRef.current, uuidv4);
    batchSubmissionRef.current = submission;
    const submittedItems = itemsToProcess.map((item, index) => ({ ...item, request_id: submission.payload.items[index].request_id }));
    setItems(submittedItems);
    submissionInFlightRef.current = true;
    setIsSubmitting(true);
    setApiError("");
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(END_POINT + "/warehouse-import/batch", submission.payload, {
        headers: { Authorization: "Bearer " + token }, timeout: 120000,
      });
      if (![200, 202].includes(response.data?.status || response.status))
        throw Object.assign(new Error(response.data?.responseText || "Không thể lưu phiếu nhập kho"), { response });
      const remaining = remainingImportItems(submittedItems, response.data?.data);
      if (remaining) {
        setItems(remaining.items);
        setIsRetryPending(remaining.uncertain);
        retryPendingRef.current = remaining.uncertain;
        if (!remaining.uncertain) batchSubmissionRef.current = null;
        setApiError(remaining.uncertain
          ? "Phản hồi chưa rõ dòng nào đã lưu. Gửi lại cùng yêu cầu để kiểm tra; không chỉnh sửa lô hàng lúc này."
          : "Đã lưu " + (submittedItems.length - remaining.items.length) + "/" + submittedItems.length + " dòng. Các dòng chưa lưu được giữ lại bên dưới.");
        if (onSuccess) onSuccess(response.data);
        return;
      }
      setIsRetryPending(false);
      retryPendingRef.current = false;
      NotificatioSucController(response.data?.responseText || (response.status === 202
        ? "Đã gửi phiếu nhập chờ duyệt; tồn kho chưa thay đổi" : "Đã nhập kho thành công"));
      toggle();
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      const uncertain = !error.response || error.response.status >= 500;
      setIsRetryPending(uncertain);
      retryPendingRef.current = uncertain;
      const message = uncertain
        ? "Chưa xác định kết quả lưu. Bấm gửi lại để kiểm tra cùng phiếu; hệ thống giữ nguyên mã yêu cầu."
        : error.response?.data?.responseText || error.message;
      setApiError(message);
      NotificatioErrController(message);
    } finally {
      submissionInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Tính tổng
  const totalQuantity = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  );
  const totalAmount = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * roundPrice(item.price),
    0,
  );
  // Tổng chiết khấu và thuế từ toàn bộ các hóa đơn
  const totalDiscount = Object.values(batchAdjustments).reduce((sum, adj) => {
    const val = adj?.discount_amount;
    return sum + (val !== "" && Number.isFinite(Number(val)) ? Number(val) : 0);
  }, 0);

  const totalTax = Object.values(batchAdjustments).reduce((sum, adj) => {
    const val = adj?.tax_amount;
    return sum + (val !== "" && Number.isFinite(Number(val)) ? Number(val) : 0);
  }, 0);

  const finalTotal = totalAmount - totalDiscount + totalTax;
  const matchedCount = items.filter((i) => i.is_matched).length;
  const unmatchedCount = items.filter((i) => !i.is_matched).length;
  const comparison = invoiceComparison(items, invoiceInfo);
  const editsDisabled = isSubmitting || isRetryPending;

  // Nhóm các mặt hàng theo từng hóa đơn (source_batch_id) để hiển thị chiết khấu & thuế riêng
  const itemBatches = React.useMemo(() => {
    const batches = [];
    const batchMap = new Map();

    items.forEach((item, index) => {
      const bId = item.source_batch_id || "default";
      if (!batchMap.has(bId)) {
        const adj = batchAdjustments[bId] || {};
        const batchObj = {
          batchId: bId,
          providerName: item.provider_name || adj.provider_name || "",
          invoiceNumber: item.invoice_number || adj.invoice_number || "",
          items: [],
        };
        batchMap.set(bId, batchObj);
        batches.push(batchObj);
      }
      batchMap.get(bId).items.push({ item, originalIndex: index });
    });

    return batches;
  }, [items, batchAdjustments]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="camera-import-modal"
      backdrop="static"
    >
      <div className="camera-import-header">
        <div className="d-flex align-items-center gap-2">
          <h5>
            <i className="simple-icon-camera mr-2" /> Nhập Hàng Camera AI & Quét Barcode
          </h5>
          <span
            className="header-badge ml-3"
            style={{
              background: "rgba(59, 130, 246, 0.25)",
              color: "#93c5fd",
              border: "1px solid rgba(147, 197, 253, 0.3)",
              fontSize: "0.85rem",
              padding: "4px 10px",
              borderRadius: "6px",
              fontWeight: 600,
            }}
          >
            <i className="simple-icon-tag mr-1" /> Mã: {receiptCode}
          </span>
        </div>

        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center mr-3">
            <span className="text-white-50 small mr-2 font-weight-bold">
              <i className="simple-icon-calendar mr-1" /> NGÀY NHẬP:
            </span>
            <input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              disabled={editsDisabled}
              className="form-control form-control-sm"
              style={{
                width: "145px",
                height: "34px",
                background: "rgba(255, 255, 255, 0.15)",
                color: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            />
          </div>

          <button
            type="button"
            className="close-btn"
            onClick={toggle}
            disabled={editsDisabled || isAnalyzing}
          >
            &times;
          </button>
        </div>
      </div>

      <ModalBody className="p-4">
        {/* 3 Tabs: Camera & Live Barcode | Quét Hóa đơn | Tải ảnh có sẵn */}
        <div className="camera-tabs">
          <button
            type="button"
            className={`camera-tab-btn ${activeTab === "camera" ? "active" : ""}`}
            disabled={editsDisabled || isAnalyzing}
            onClick={() => {
              setActiveTab("camera");
              setCapturedImage(null);
            }}
          >
            <i className="simple-icon-camrecorder" /> Máy ảnh & Quét Barcode
            Live
          </button>
          <button
            type="button"
            className={`camera-tab-btn ${activeTab === "invoice" ? "active" : ""}`}
            disabled={editsDisabled || isAnalyzing}
            onClick={() => {
              setActiveTab("invoice");
              setCapturedImage(null);
            }}
          >
            <i className="simple-icon-doc" /> Quét Hóa đơn / Phiếu giao hàng
          </button>
          <button
            type="button"
            className={`camera-tab-btn ${activeTab === "upload" ? "active" : ""}`}
            disabled={editsDisabled || isAnalyzing}
            onClick={() => {
              setActiveTab("upload");
              setCapturedImage(null);
            }}
          >
            <i className="simple-icon-cloud-upload" /> Tải ảnh từ thiết bị
          </button>
        </div>

        {/* View Camera hoặc Upload */}
        {!capturedImage && !isAnalyzing && (
          <div>
            {/* Tab 1: Live Camera + Live Barcode */}
            {activeTab === "camera" && (
              <div>
                <div
                  className={`camera-viewfinder-container ${
                    isBarcodeFlashing ? "barcode-flash-success" : ""
                  }`}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera-video-stream"
                  />
                  <div className="camera-scanline-overlay">
                    <div className="camera-reticle" />
                  </div>
                </div>
                <canvas ref={canvasRef} style={{ display: "none" }} />

                {/* Thanh trạng thái Live Barcode */}
                <div className="live-barcode-status-bar">
                  <div className="live-barcode-pill">
                    <span className="live-barcode-dot" />
                    <span>{isCameraActive && isLiveScanning && barcodeSupported
                      ? "Đang tự động quét mã vạch (Live Scan 0đ)"
                      : cameraError ? "Camera chưa sẵn sàng"
                      : !barcodeSupported ? "Trình duyệt chưa hỗ trợ quét mã vạch trực tiếp"
                      : "Quét mã vạch đang tạm dừng"}</span>
                  </div>
                  <div>
                    {lastScannedCode ? (
                      <span className="badge badge-success">
                        Mã vừa quét: {lastScannedCode}
                      </span>
                    ) : (
                      <small className="text-muted">
                        Đưa mã vạch vào khung để tự động nhận diện
                      </small>
                    )}
                  </div>
                </div>

                {cameraError ? (
                  <Alert color="warning" className="mt-3 text-center">
                    {cameraError}
                  </Alert>
                ) : (
                  <div className="camera-controls-bar">
                    <button
                      type="button"
                      className="btn-capture-photo"
                      onClick={handleCapturePhoto}
                      disabled={!isCameraActive || editsDisabled || isAnalyzing}
                      title="Chụp để AI nhận diện toàn bộ hình ảnh sản phẩm"
                    >
                      <i className="simple-icon-bulb" /> Chụp & Quét AI
                    </button>
                    <Button
                      color="secondary"
                      outline
                      onClick={switchCameraFacing}
                      title="Đổi camera trước/sau"
                    >
                      <i className="simple-icon-refresh mr-1" /> Đổi camera
                    </Button>
                    <Button
                      color={isLiveScanning ? "success" : "secondary"}
                      outline
                      onClick={() => setIsLiveScanning(!isLiveScanning)}
                      title="Bật/Tắt chế độ tự quét mã vạch"
                    >
                      <i className="simple-icon-speedometer mr-1" />{" "}
                      {isLiveScanning
                        ? "Auto Barcode: Bật"
                        : "Auto Barcode: Tắt"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Quét Hóa đơn nhập hàng (Invoice OCR) */}
            {activeTab === "invoice" && (
              <div>
                <div className="camera-viewfinder-container">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera-video-stream"
                  />
                  <div className="camera-scanline-overlay">
                    <div className="camera-reticle invoice-doc" />
                  </div>
                </div>
                <canvas ref={canvasRef} style={{ display: "none" }} />

                {cameraError && <Alert color="warning" className="mt-3">{cameraError}</Alert>}
                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                  <button
                    type="button"
                    className="btn-capture-photo"
                    onClick={handleCapturePhoto}
                    disabled={!isCameraActive || editsDisabled || isAnalyzing}
                  >
                    <i className="simple-icon-doc" /> Chụp & Đọc bảng kê Hóa đơn
                  </button>
                  <Button
                    color="info"
                    outline
                    className="ml-2"
                    onClick={() =>
                      invoiceFileInputRef.current &&
                      invoiceFileInputRef.current.click()
                    }
                  >
                    <i className="simple-icon-cloud-upload mr-1" /> Tải ảnh hóa
                    đơn
                  </Button>
                  <input
                    ref={invoiceFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp" multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(e, "invoice")}
                  />
                </div>
              </div>
            )}

            {/* Tab 3: Upload ảnh có sẵn */}
            {activeTab === "upload" && (
              <div>
                <div className="mb-3 d-flex align-items-center" style={{ gap: 12 }}>
                  <label htmlFor="camera-upload-mode" className="mb-0">Loại ảnh:</label>
                  <Input id="camera-upload-mode" type="select" value={uploadMode}
                    onChange={(event) => setUploadMode(event.target.value)} style={{ maxWidth: 260 }}>
                    <option value="invoice">Hóa đơn / phiếu giao hàng</option>
                    <option value="product">Ảnh sản phẩm / bao bì</option>
                  </Input>
                </div>
                <div
                  className="camera-dropzone"
                  role="button" tabIndex={0} aria-label="Chọn ảnh từ thiết bị"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => { event.preventDefault(); handleFiles(event.dataTransfer.files, uploadMode); }}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click(); }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="simple-icon-picture" />
                  <h5>Kéo thả hoặc bấm để chọn ảnh sản phẩm / hóa đơn</h5>
                  <p className="text-muted mb-0">
                    Hỗ trợ định dạng JPG, PNG. Chụp rõ bao bì, mã vạch hoặc bảng
                    kê hóa đơn.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp" multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(e, uploadMode)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trạng thái đang phân tích AI */}
        {isAnalyzing && (
          <div className="ai-scanning-badge">
            <div className="ai-pulse-scanner">
              <i className="simple-icon-bulb" />
            </div>
            <h5>AI đang quét và trích xuất dữ liệu...</h5>
            <p className="text-muted">
              Hệ thống đang tự động bóc tách tên sản phẩm, mã vạch, số lượng,
              đơn giá và đối chiếu kho.
            </p>
          </div>
        )}

        {/* Lỗi hiển thị nếu có */}
        {apiError && !isAnalyzing && (
          <Alert color="danger" className="mt-3">
            <i className="simple-icon-exclamation mr-2" />
            {apiError}
          </Alert>
        )}

        {!isAnalyzing && items.length === 0 && <Button color="secondary" outline disabled={editsDisabled} onClick={handleAddManualRow}>
          Thêm dòng thủ công
        </Button>}
        <div className={sourceImages.length && !isAnalyzing ? "camera-review-layout" : ""}>
          {sourceImages.length > 0 && !isAnalyzing && <aside className="camera-source-preview" style={{ display: "none" }}>
            <strong>Ảnh gốc để đối chiếu</strong>
            <div className="camera-source-picker">{sourceImages.map((image, index) =>
              <Button key={image.id} size="sm" outline color="primary" onClick={() => setCapturedImage(image.image)}>
                Ảnh {index + 1}
              </Button>)}</div>
            <img src={capturedImage || sourceImages[sourceImages.length - 1].image} alt="Ảnh gốc hóa đơn hoặc sản phẩm đã quét" />
          </aside>}
          <div className="camera-review-content" data-locked={editsDisabled}><fieldset disabled={editsDisabled}>
        {/* Banner & Form thông tin hóa đơn & Nhà cung cấp */}
        {(invoiceInfo || items.length > 0) && (
          (() => {
            const hasSeller = Boolean(
              invoiceInfo?.has_seller !== false &&
              (invoiceInfo?.provider_name || providerName)
            );
            const displayProviderName = invoiceInfo?.provider_name || providerName || invoiceForm.provider_name || "";

            return (
              <div
                className={`invoice-extracted-banner ${!hasSeller ? "invoice-banner-warning" : ""}`}
                style={
                  !hasSeller
                    ? {
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        marginBottom: "16px",
                      }
                    : undefined
                }
              >
                <div
                  className="d-flex align-items-center justify-content-between w-100 flex-wrap"
                  style={{ gap: "10px" }}
                >
                  <div className="invoice-extracted-title" style={!hasSeller ? { color: "#b45309" } : undefined}>
                    <i
                      className={`${!hasSeller ? "simple-icon-info text-warning" : "simple-icon-badge text-primary"}`}
                      style={{ fontSize: "1.1rem" }}
                    />
                    <span>
                      {hasSeller
                        ? "Thông tin Nhà Cung Cấp & Hóa Đơn:"
                        : "Chưa xác định được Nhà Cung Cấp:"}
                    </span>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <Button
                      color={hasSeller ? "primary" : "warning"}
                      size="sm"
                      outline={hasSeller}
                      onClick={() => setIsEditingInvoice(!isEditingInvoice)}
                      title="Chỉnh sửa hoặc nhập thông tin Nhà Cung Cấp"
                      style={{
                        borderRadius: "6px",
                        fontWeight: 600,
                        padding: "4px 12px",
                        fontSize: "0.82rem",
                      }}
                    >
                      <i className="simple-icon-pencil mr-1" />
                      {hasSeller
                        ? isEditingInvoice
                          ? "Đóng sửa"
                          : "Sửa thông tin NCC"
                        : isEditingInvoice
                        ? "Ẩn form nhập NCC"
                        : "+ Nhập thông tin NCC"}
                    </Button>
                    {items.length > 0 && displayProviderName && (
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleApplyInvoiceToAllItems}
                        title="Gán nhà cung cấp này vào tất cả các dòng sản phẩm trong bảng"
                        style={{
                          borderRadius: "6px",
                          fontWeight: 600,
                          padding: "4px 12px",
                          fontSize: "0.82rem",
                        }}
                      >
                        <i className="simple-icon-check mr-1" />
                        Áp dụng NCC cho tất cả dòng
                      </Button>
                    )}
                  </div>
                </div>

                {!hasSeller && (
                  <div className="small text-muted mt-1 w-100">
                    <i className="simple-icon-bulb mr-1 text-warning" />
                    Hệ thống không tự động điền để tránh lấy nhầm thông tin Người mua. Bạn hãy chọn NCC có sẵn hoặc tự nhập tên NCC bên dưới:
                  </div>
                )}

                {/* Các thẻ thông tin rút gọn nếu có */}
                <div className="d-flex flex-wrap gap-2 mt-2 w-100">
                  <span className="invoice-meta-tag">
                    NCC: <strong>{displayProviderName || "Chưa có (Hãy tự nhập)"}</strong>
                  </span>
                  {(invoiceInfo?.tax_code || invoiceForm.tax_code) && (
                    <span className="invoice-meta-tag">
                      MST: <strong>{invoiceInfo?.tax_code || invoiceForm.tax_code}</strong>
                    </span>
                  )}
                  {(invoiceInfo?.provider_phone || invoiceForm.provider_phone) && (
                    <span className="invoice-meta-tag">
                      SĐT: <strong>{invoiceInfo?.provider_phone || invoiceForm.provider_phone}</strong>
                    </span>
                  )}
                  {(invoiceInfo?.provider_address || invoiceForm.provider_address) && (
                    <span className="invoice-meta-tag">
                      Địa chỉ: <strong>{invoiceInfo?.provider_address || invoiceForm.provider_address}</strong>
                    </span>
                  )}
                  {(invoiceInfo?.invoice_number || invoiceForm.invoice_number) && (
                    <span className="invoice-meta-tag">
                      Số HĐ: <strong>{invoiceInfo?.invoice_number || invoiceForm.invoice_number}</strong>
                    </span>
                  )}
                  {invoiceInfo?.invoice_date && (
                    <span className="invoice-meta-tag">
                      Ngày HĐ: <strong>{invoiceInfo.invoice_date}</strong>
                    </span>
                  )}
                </div>

                {/* Form chỉnh sửa hoặc tự nhập thông tin Nhà cung cấp */}
                {isEditingInvoice && (
                  <div
                    className="w-100 mt-3 p-3"
                    style={{
                      background: "#ffffff",
                      border: !hasSeller ? "1px solid #fde68a" : "1px solid #93c5fd",
                      borderRadius: "10px",
                      boxShadow: !hasSeller
                        ? "0 4px 12px rgba(245, 158, 11, 0.12)"
                        : "0 4px 12px rgba(59, 130, 246, 0.12)",
                    }}
                  >
                    <div
                      className={`font-weight-bold mb-2 d-flex align-items-center ${!hasSeller ? "text-warning" : "text-primary"}`}
                      style={{ fontSize: "0.9rem" }}
                    >
                      <i className="simple-icon-pencil mr-1" />
                      {hasSeller
                        ? "Cập nhật thông tin Nhà Cung Cấp & Hóa Đơn (Tự động lưu vào Danh sách NCC khi nhập kho)"
                        : "Tự nhập thông tin Nhà Cung Cấp cho hóa đơn này:"}
                    </div>
                    <Row>
                      <Col md="5" className="mb-2">
                        <Label className="small font-weight-bold mb-1 text-dark">
                          Tên Nhà Cung Cấp <span className="text-danger">*</span>:
                        </Label>
                        <Creatable
                          className="react-select-sm w-100"
                          classNamePrefix="react-select"
                          placeholder="Chọn NCC có sẵn hoặc gõ tên NCC mới..."
                          options={providerOptions}
                          value={
                            invoiceForm.provider_name
                              ? { value: invoiceForm.provider_name, label: invoiceForm.provider_name }
                              : null
                          }
                          onChange={handleSelectProviderInForm}
                          isClearable
                          formatCreateLabel={(val) => `+ NCC mới: "${val}"`}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({
                              ...base,
                              minHeight: 34,
                              height: 34,
                              fontSize: "0.85rem",
                              borderColor: "#cbd5e1",
                              borderRadius: 6,
                            }),
                          }}
                        />
                      </Col>
                      <Col md="4" className="mb-2">
                        <Label className="small font-weight-bold mb-1 text-dark">Mã số thuế (MST):</Label>
                        <Input
                          type="text"
                          className="form-control-sm"
                          value={invoiceForm.tax_code}
                          placeholder="VD: 0101234567..."
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_code: e.target.value })}
                          style={{ height: "34px", borderRadius: "6px" }}
                        />
                      </Col>
                      <Col md="3" className="mb-2">
                        <Label className="small font-weight-bold mb-1 text-dark">Số hóa đơn / Phiếu:</Label>
                        <Input
                          type="text"
                          className="form-control-sm"
                          value={invoiceForm.invoice_number}
                          placeholder="VD: HD001, PT-9988..."
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                          style={{ height: "34px", borderRadius: "6px" }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4" className="mb-2">
                        <Label className="small font-weight-bold mb-1 text-dark">Số điện thoại NCC:</Label>
                        <Input
                          type="text"
                          className="form-control-sm"
                          value={invoiceForm.provider_phone}
                          placeholder="SĐT liên hệ..."
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, provider_phone: e.target.value })}
                          style={{ height: "34px", borderRadius: "6px" }}
                        />
                      </Col>
                      <Col md="5" className="mb-2">
                        <Label className="small font-weight-bold mb-1 text-dark">Địa chỉ NCC:</Label>
                        <Input
                          type="text"
                          className="form-control-sm"
                          value={invoiceForm.provider_address}
                          placeholder="Địa chỉ nhà cung cấp..."
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, provider_address: e.target.value })}
                          style={{ height: "34px", borderRadius: "6px" }}
                        />
                      </Col>
                      <Col md="3" className="mb-2">
                        <Label className="small font-weight-bold mb-1 text-dark">Ghi chú phiếu:</Label>
                        <Input
                          type="text"
                          className="form-control-sm"
                          value={invoiceForm.note}
                          placeholder="Ghi chú thêm..."
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, note: e.target.value })}
                          style={{ height: "34px", borderRadius: "6px" }}
                        />
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top flex-wrap" style={{ gap: "8px" }}>
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="syncInvoiceCheckbox"
                          checked={syncInvoiceToAll}
                          onChange={(e) => setSyncInvoiceToAll(e.target.checked)}
                        />
                        <label
                          className="custom-control-label small font-weight-bold text-primary"
                          htmlFor="syncInvoiceCheckbox"
                          style={{ cursor: "pointer" }}
                        >
                          Tự động gán tên NCC này cho tất cả {items.length} sản phẩm trong bảng
                        </label>
                      </div>
                      <div className="d-flex" style={{ gap: "8px" }}>
                        <Button color="secondary" size="sm" outline onClick={() => setIsEditingInvoice(false)}>
                          Hủy
                        </Button>
                        <Button color="success" size="sm" className="font-weight-bold" onClick={handleSaveInvoiceForm}>
                          <i className="simple-icon-check mr-1" /> Lưu & Áp dụng vào bảng
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}


        {/* Bảng kết quả sau khi nhận diện */}
        {items.length > 0 && !isAnalyzing && (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 font-weight-bold">
                <i className="simple-icon-list mr-1" /> Danh sách sản phẩm nhập
                ({items.length})
              </h6>
              <div className="d-flex gap-2">
                <Button
                  color="info"
                  size="sm"
                  outline
                  disabled={editsDisabled} onClick={handleRetakeOrAddMore}
                >
                  <i className="simple-icon-camera mr-1" /> Quét thêm ảnh
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  outline
                  disabled={editsDisabled} onClick={handleAddManualRow}
                  className="ml-2"
                >
                  <i className="simple-icon-plus mr-1" /> Thêm dòng
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  outline
                  disabled={editsDisabled} onClick={handleClearAllAndReset}
                  className="ml-2"
                  title="Xóa danh sách và dữ liệu hóa đơn hiện tại để bắt đầu quét đợt mới"
                >
                  <i className="simple-icon-refresh mr-1" /> Làm mới đợt quét
                </Button>
              </div>
            </div>

            {/* Thống kê nhanh */}
            <div className="camera-results-summary">
              <div className="summary-pill total">
                <span>Tổng mặt hàng</span>
                <strong>{items.length} món</strong>
              </div>
              <div className="summary-pill matched">
                <span>Đã khớp kho</span>
                <strong className="text-success">{matchedCount} món</strong>
              </div>
              <div className="summary-pill unmatched">
                <span>Hàng mới tạo</span>
                <strong className="text-warning">{unmatchedCount} món</strong>
              </div>
              <div className="summary-pill">
                <span>Tổng tiền hàng</span>
                <strong className="text-primary">
                  {formatNumberWithDot(totalAmount)} đ
                </strong>
              </div>
              {(totalDiscount > 0 || totalTax > 0) && (
                <div className="summary-pill" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                  <span>Thực trả (Sau CK & Thuế)</span>
                  <strong style={{ color: "#15803d" }}>
                    {formatNumberWithDot(finalTotal)} đ
                  </strong>
                </div>
              )}
            </div>

            {/* Bảng sản phẩm */}
            <div className="table-responsive">
              <table className="camera-items-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }} className="text-center">#</th>
                    <th style={{ width: "125px" }}>Đối chiếu kho</th>
                    <th style={{ width: "135px" }}>Mã vạch</th>
                    <th style={{ minWidth: "200px" }}>Tên mặt hàng</th>
                    <th style={{ width: "85px" }} className="text-center">ĐVT</th>
                    <th style={{ width: "90px" }} className="text-center">Số lượng</th>
                    <th style={{ width: "115px" }} className="text-right">Đơn giá (đ)</th>
                    <th style={{ width: "120px" }} className="text-right">Thành tiền</th>
                    <th style={{ width: "120px" }} className="text-right">Giá bán lẻ (đ)</th>
                    <th style={{ width: "220px" }}>Nhà cung cấp</th>
                    <th style={{ width: "160px" }}>Ghi chú</th>
                    <th style={{ width: "130px" }}>Hạn dùng</th>
                    <th style={{ width: "45px" }} className="text-center"></th>
                  </tr>
                </thead>
                <tbody>
                  {itemBatches.map((batch, batchIdx) => {
                    const batchItems = batch.items.map((b) => b.item);
                    const batchSubtotal = batchItems.reduce(
                      (sum, it) => sum + (Number(it.quantity) || 0) * roundPrice(it.price),
                      0
                    );
                    const adj = batchAdjustments[batch.batchId] || {};
                    const bDiscount =
                      adj.discount_amount !== "" && Number.isFinite(Number(adj.discount_amount))
                        ? Number(adj.discount_amount)
                        : 0;
                    const bTax =
                      adj.tax_amount !== "" && Number.isFinite(Number(adj.tax_amount))
                        ? Number(adj.tax_amount)
                        : 0;
                    const bFinal = batchSubtotal - bDiscount + bTax;

                    return (
                      <React.Fragment key={batch.batchId || batchIdx}>
                        {itemBatches.length > 1 && (
                          <tr
                            style={{
                              background: "#f1f5f9",
                              borderTop: batchIdx > 0 ? "3px solid #cbd5e1" : "none",
                            }}
                          >
                            <td colSpan={13} className="py-2 px-3 font-weight-bold text-dark" style={{ fontSize: "0.9rem" }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span>
                                  <i className="simple-icon-doc mr-1 text-primary" />
                                  <strong>Hóa đơn #{batchIdx + 1}:</strong>{" "}
                                  {batch.providerName ? batch.providerName : "Chưa có NCC"}
                                  {batch.invoiceNumber && (
                                    <span className="text-muted ml-2 font-weight-normal">
                                      (Số HĐ: {batch.invoiceNumber})
                                    </span>
                                  )}
                                </span>
                                <span className="badge badge-light border">
                                  {batch.items.length} mặt hàng • Tiền hàng: {formatNumberWithDot(batchSubtotal)} đ
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}

                        {batch.items.map(({ item, originalIndex }) => {
                          const idx = originalIndex;
                          const rowTotal =
                            (Number(item.quantity) || 0) * roundPrice(item.price);

                          return (
                            <tr key={item.id || idx}>
                        <td className="text-center font-weight-bold text-muted">
                          {idx + 1}
                        </td>
                        <td>
                          {item.is_matched && !item.requires_match_confirmation ? (
                            <span className="match-badge matched">
                              <i className="simple-icon-check" />{" "}
                              {item.match_type === "user_confirmed"
                                ? "Đã xác nhận"
                                : `Khớp ${getMatchPercent(item)}%`}
                            </span>
                          ) : (
                            <span className="match-badge unmatched">
                              <i className="simple-icon-star" /> {item.requires_match_confirmation ? "Cần xác nhận" : "Chưa ghép kho"}
                            </span>
                          )}
                          {!item.is_matched && item.confidence != null && (
                            <small className="d-block text-muted">
                              AI: {Math.round(item.confidence * 100)}% (tham khảo)
                            </small>
                          )}
                          {(item.candidate_suggestions || []).length > 0 && <div className="camera-stock-suggestions">
                            {item.candidate_suggestions.map((stock) => <div key={stock.id}>
                              <small>{stock.name} · {stock.unit}</small>
                              {String(item.unit).trim().toLowerCase() !== String(stock.unit).trim().toLowerCase() &&
                                <Input type="number" min="0.001" step="any" disabled={editsDisabled}
                                  aria-label={"Số " + stock.unit + " trong 1 " + item.unit}
                                  placeholder={stock.unit + " / " + item.unit} value={conversionFactors[item.id] || ""}
                                  onChange={(event) => setConversionFactors((prev) => ({ ...prev, [item.id]: event.target.value }))} />}
                              <Button size="sm" color="info" outline disabled={editsDisabled} onClick={() => handleChooseStock(idx, stock)}>Chọn / Quy đổi</Button>
                            </div>)}
                          </div>}
                          {item.requires_match_confirmation && <Button size="sm" color="secondary" outline disabled={editsDisabled}
                            onClick={() => { batchSubmissionRef.current = null; setItems((prev) => prev.map((row, index) => index === idx
                              ? { ...row, stock_id: null, stock_selection: "new", is_matched: false, requires_match_confirmation: false, request_id: undefined }
                              : { ...row, request_id: undefined })); }}>Xác nhận hàng mới</Button>}
                        </td>
                        <td style={{ width: "135px" }}>
                          <Input
                            type="text"
                            value={item.barcode || ""}
                            onChange={(e) =>
                              handleItemChange(idx, "barcode", e.target.value)
                            }
                            placeholder="Mã vạch..."
                            disabled={editsDisabled}
                          />
                        </td>
                        <td style={{ minWidth: "200px" }}>
                          <Input
                            type="text"
                            value={item.name || ""}
                            onChange={(e) =>
                              handleItemChange(idx, "name", e.target.value)
                            }
                            placeholder="Tên sản phẩm..."
                            disabled={editsDisabled}
                          />
                          {item.submission_error && <small className="text-danger d-block">{item.submission_error}</small>}
                        </td>
                        <td style={{ width: "85px" }}>
                          <Input
                            type="text"
                            className="text-center"
                            value={item.unit || ""}
                            onChange={(e) =>
                              handleItemChange(idx, "unit", e.target.value)
                            }
                            placeholder="ĐVT"
                            disabled={editsDisabled}
                          />
                        </td>
                        <td style={{ width: "90px" }}>
                          <Input
                            type="number"
                            min="0.001"
                            step="0.001"
                            className="text-center font-weight-bold"
                            value={item.quantity}
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onChange={(e) =>
                              handleItemChange(idx, "quantity", e.target.value)
                            }
                            disabled={editsDisabled}
                          />
                        </td>
                        <td style={{ width: "115px" }}>
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="text-right font-weight-bold"
                            value={formatNumberWithDot(item.price)}
                            placeholder="0"
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onChange={(e) =>
                              handleItemChange(idx, "price", e.target.value)
                            }
                            disabled={editsDisabled}
                          />
                          {item.price_source === "history" && <small className="text-warning d-block">Giá từ lịch sử, cần đối chiếu</small>}
                        </td>
                        <td className="text-right font-weight-bold text-primary" style={{ width: "120px" }}>
                          {formatNumberWithDot(rowTotal)} đ
                          {item.source_line_total != null && Math.abs(rowTotal - item.source_line_total) > 1 &&
                            <small className="text-warning d-block">Trên ảnh: {formatNumberWithDot(item.source_line_total)} đ</small>}
                        </td>
                        <td style={{ width: "120px" }}>
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="text-right"
                            placeholder="Giữ giá bán hiện tại"
                            value={formatNumberWithDot(item.retail_price)}
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onChange={(e) =>
                              handleItemChange(idx, "retail_price", e.target.value)
                            }
                            disabled={editsDisabled}
                          />
                        </td>
                        <td style={{ width: "220px" }}>
                          <Creatable
                            className="react-select-sm w-100"
                            classNamePrefix="react-select"
                            placeholder="Chọn/nhập NCC..."
                            options={providerOptions}
                            value={
                              item.provider_name
                                ? {
                                    value: item.provider_name,
                                    label: item.provider_name,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              handleItemChange(
                                idx,
                                "provider_name",
                                opt ? opt.value : "",
                              )
                            }
                            isDisabled={editsDisabled}
                            isClearable
                            formatCreateLabel={(inputValue) =>
                              `+ NCC mới: "${inputValue}"`
                            }
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              container: (base) => ({ ...base, width: "100%" }),
                              control: (base) => ({
                                ...base,
                                minHeight: 35,
                                height: 35,
                                borderColor: "#cbd5e1",
                                borderRadius: 6,
                                fontSize: "0.85rem",
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: "0 8px",
                              }),
                            }}
                          />
                        </td>
                        <td style={{ width: "160px" }}>
                          <Input
                            type="text"
                            placeholder="Ghi chú dòng..."
                            value={item.note || ""}
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onChange={(e) =>
                              handleItemChange(idx, "note", e.target.value)
                            }
                            disabled={editsDisabled}
                            style={{
                              fontSize: "0.85rem",
                              height: "35px",
                              borderRadius: "6px",
                            }}
                          />
                        </td>
                        <td style={{ width: "130px" }}>
                          <Input
                            type="date"
                            value={item.expiry_date || ""}
                            onChange={(e) =>
                              handleItemChange(idx, "expiry_date", e.target.value)
                            }
                            disabled={editsDisabled}
                            style={{
                              fontSize: "0.85rem",
                              height: "35px",
                              borderRadius: "6px",
                            }}
                          />
                        </td>
                        <td className="text-center" style={{ width: "45px" }}>
                          <Button
                            color="link"
                            className="p-0 text-danger"
                            onClick={() => handleRemoveItem(idx)}
                            disabled={editsDisabled}
                            title="Xóa dòng này"
                          >
                            <i className="simple-icon-trash" style={{ fontSize: "1.1rem" }} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}

                  {/* 2 Hàng hiển thị Chiết khấu và Thuế phí của riêng hóa đơn này */}
                  <tr style={{ background: "#fffdf5", borderTop: "2px solid #e2e8f0" }}>
                    <td className="text-center text-muted font-weight-bold">
                      <i className="simple-icon-tag text-warning" />
                    </td>
                    <td colSpan={6} className="font-weight-bold text-right align-middle" style={{ color: "#b45309" }}>
                      {itemBatches.length > 1
                        ? `Chiết khấu (Hóa đơn #${batchIdx + 1}):`
                        : "Chiết khấu hóa đơn (nếu có):"}
                    </td>
                    <td className="text-right align-middle" style={{ width: "120px" }}>
                      <Input
                        type="number"
                        min="0"
                        value={adj.discount_amount ?? ""}
                        onChange={(e) =>
                          handleBatchAdjustmentChange(
                            batch.batchId,
                            "discount_amount",
                            e.target.value
                          )
                        }
                        placeholder="0"
                        disabled={editsDisabled}
                        className="text-right font-weight-bold"
                        style={{ height: "35px", borderRadius: "6px", color: "#b45309" }}
                      />
                    </td>
                    <td colSpan={5} className="align-middle" style={{ fontSize: "0.82rem", color: "#64748b" }}>
                      <span className="d-flex align-items-center">
                        <i className="simple-icon-info mr-1 text-primary" style={{ fontSize: "0.95rem" }} />
                        <span>Tiền chiết khấu sẽ được <strong>chia đều (giảm trừ)</strong> vào đơn giá các sản phẩm của hóa đơn này khi lưu kho.</span>
                      </span>
                    </td>
                  </tr>

                  <tr style={{ background: "#f0fdf4" }}>
                    <td className="text-center text-muted font-weight-bold">
                      <i className="simple-icon-calculator text-success" />
                    </td>
                    <td colSpan={6} className="font-weight-bold text-right align-middle" style={{ color: "#15803d" }}>
                      {itemBatches.length > 1
                        ? `Thuế phí / VAT (Hóa đơn #${batchIdx + 1}):`
                        : "Thuế phí / VAT (nếu có):"}
                    </td>
                    <td className="text-right align-middle" style={{ width: "120px" }}>
                      <Input
                        type="number"
                        min="0"
                        value={adj.tax_amount ?? ""}
                        onChange={(e) =>
                          handleBatchAdjustmentChange(
                            batch.batchId,
                            "tax_amount",
                            e.target.value
                          )
                        }
                        placeholder="0"
                        disabled={editsDisabled}
                        className="text-right font-weight-bold"
                        style={{ height: "35px", borderRadius: "6px", color: "#15803d" }}
                      />
                    </td>
                    <td colSpan={5} className="align-middle" style={{ fontSize: "0.82rem", color: "#64748b" }}>
                      <span className="d-flex align-items-center">
                        <i className="simple-icon-info mr-1 text-primary" style={{ fontSize: "0.95rem" }} />
                        <span>Thuế phí sẽ được <strong>chia đều (cộng thêm)</strong> vào đơn giá các sản phẩm của hóa đơn này khi lưu kho.</span>
                      </span>
                    </td>
                  </tr>

                  {(bDiscount > 0 || bTax > 0) && (
                    <tr style={{ background: "#f8fafc", fontWeight: "bold" }}>
                      <td colSpan={7} className="text-right align-middle text-primary" style={{ fontSize: "0.92rem" }}>
                        {itemBatches.length > 1
                          ? `Thực trả Hóa đơn #${batchIdx + 1} (sau CK & Thuế):`
                          : "Tổng thanh toán thực tế (sau CK & Thuế):"}
                      </td>
                      <td className="text-right align-middle text-primary" style={{ fontSize: "0.95rem" }}>
                        {formatNumberWithDot(bFinal)} đ
                      </td>
                      <td colSpan={5} className="align-middle text-muted" style={{ fontSize: "0.82rem" }}>
                        = {formatNumberWithDot(batchSubtotal)}đ (tiền hàng){" "}
                        {bDiscount > 0 ? `- ${formatNumberWithDot(bDiscount)}đ (CK) ` : ""}
                        {bTax > 0 ? `+ ${formatNumberWithDot(bTax)}đ (thuế)` : ""}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
              </table>
            </div>
          </div>
        )}
          </fieldset></div>
        </div>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <div>
          {items.length > 0 && (
            <span className="text-muted">
              Đã sẵn sàng nhập: <strong>{items.length}</strong> sản phẩm (Tổng
              SL: <strong>{totalQuantity}</strong>)
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={toggle} disabled={editsDisabled || isAnalyzing}>
            Hủy bỏ
          </Button>
          <Button
            color="primary"
            onClick={handleSubmitBatch}
            disabled={isSubmitting || isAnalyzing || items.length === 0}
            className="ml-2 font-weight-bold"
          >
            {isSubmitting ? (
              <span>
                <i className="simple-icon-refresh spin mr-1" /> Đang lưu kho...
              </span>
            ) : (
              <span>
                <i className="simple-icon-check mr-1" /> {isRetryPending ? "Gửi lại cùng phiếu" : applyOnly ? "Áp dụng vào phiếu" : "Xác nhận & Nhập kho"} (
                {formatNumberWithDot(finalTotal)} đ)
              </span>
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default CameraImportModal;
